import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Plus, 
  Users,
  UserPlus,
  Shield,
  MoreVertical,
  Pencil,
  Trash2,
  Loader2,
  Mail
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const ROLE_LABELS = {
  admin: 'Administrator',
  finance: 'Finance',
  creative: 'Creative',
  project_manager: 'Project Manager'
};

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-800',
  finance: 'bg-green-100 text-green-800',
  creative: 'bg-purple-100 text-purple-800',
  project_manager: 'bg-blue-100 text-blue-800'
};

const UsersPage = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'creative',
    is_active: true
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role: user.role || 'creative',
        is_active: user.is_active !== false
      });
    } else {
      setEditingUser(null);
      setFormData({ name: '', email: '', password: '', role: 'creative', is_active: true });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingUser && (!formData.name || !formData.email || !formData.password)) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (editingUser && !formData.name) {
      toast.error('Please enter a name');
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        // Build update payload - only include changed fields
        const updatePayload = {
          name: formData.name,
          role: formData.role,
          is_active: formData.is_active
        };
        
        // Include email if changed
        if (formData.email && formData.email !== editingUser.email) {
          updatePayload.email = formData.email;
        }
        
        // Include password if provided
        if (formData.password) {
          updatePayload.password = formData.password;
        }
        
        await axios.put(`${API}/users/${editingUser.id}`, updatePayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('User updated');
      } else {
        await axios.post(`${API}/auth/register`, {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('User created');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser.id) {
      toast.error("You cannot delete your own account");
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">User Management</h1>
          <p className="text-slate-500 mt-1">Manage team members and access permissions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#1F3C4F] hover:bg-[#152a38]"
              onClick={() => handleOpenDialog()}
              data-testid="add-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingUser ? 'Edit User' : 'Add User'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="Full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email {!editingUser && '*'}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="user-email-input"
                />
                {editingUser && (
                  <p className="text-xs text-slate-500">Leave unchanged to keep current email</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">
                  {editingUser ? 'New Password' : 'Password *'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={editingUser ? "Leave blank to keep current" : "Set password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="user-password-input"
                />
                {editingUser && (
                  <p className="text-xs text-slate-500">Leave blank to keep current password</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select 
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrator</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="creative">Creative</SelectItem>
                    <SelectItem value="project_manager">Project Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editingUser && (
                <div className="flex items-center justify-between py-2">
                  <div>
                    <Label>Active Status</Label>
                    <p className="text-sm text-slate-500">Allow user to access the system</p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#1F3C4F] hover:bg-[#152a38]"
                data-testid="user-submit-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingUser ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-4 px-6 font-heading font-semibold text-slate-700">User</th>
                  <th className="text-left py-4 px-6 font-heading font-semibold text-slate-700">Role</th>
                  <th className="text-left py-4 px-6 font-heading font-semibold text-slate-700">Status</th>
                  <th className="text-left py-4 px-6 font-heading font-semibold text-slate-700">Joined</th>
                  <th className="text-right py-4 px-6 font-heading font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr 
                    key={user.id} 
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`user-row-${user.id}`}
                  >
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1F3C4F]/10 flex items-center justify-center">
                          <span className="text-[#1F3C4F] font-heading font-bold">
                            {user.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {user.name}
                            {user.id === currentUser.id && (
                              <span className="ml-2 text-xs text-slate-400">(You)</span>
                            )}
                          </p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <Badge className={ROLE_COLORS[user.role]}>
                        {ROLE_LABELS[user.role] || user.role}
                      </Badge>
                    </td>
                    <td className="py-4 px-6">
                      <Badge className={user.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {user.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-4 px-6 text-slate-500 text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenDialog(user)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {user.id !== currentUser.id && (
                            <DropdownMenuItem 
                              onClick={() => handleDelete(user.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Role Descriptions */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Role Permissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-red-50 rounded-lg">
              <h4 className="font-heading font-semibold text-red-800">Administrator</h4>
              <p className="text-sm text-red-700 mt-1">
                Full access to all features including user management, settings, and all data export.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-heading font-semibold text-green-800">Finance</h4>
              <p className="text-sm text-green-700 mt-1">
                Access to all expense and time data, export capabilities, and financial reports.
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-heading font-semibold text-blue-800">Project Manager</h4>
              <p className="text-sm text-blue-700 mt-1">
                Full job management, time tracking, customer access, and project-related expenses.
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-heading font-semibold text-purple-800">Creative</h4>
              <p className="text-sm text-purple-700 mt-1">
                Access to job details, file uploads, notes, and personal time/expense tracking.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersPage;
